import { LockClosedIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { useDiscovery } from '../../lib/discovery';

// Module routes don't carry a display name (only their raw dot-namespaced
// featureKey — ModuleRoute.featureKeys, host/types.ts). Humanize it for the
// teaser label rather than showing the raw key to the user.
const humanizeFeatureKey = (key: string): string =>
  key
    .split('.')
    .map((part) => part.replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
    .join(' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// RouteDenied (D44, story 2-45) — the in-app "you don't have access" surface a
// denied module/app route renders INSTEAD of falling through the SPA's `*`
// catch-all to the marketing landing page. Bouncing an authenticated user to
// `/` looks like a logout and hides WHY; this renders as the route's own
// element, so it sits inside the already-mounted AppShell (nav/topbar/sidebar
// stay put — it's obviously not a logout).
//
// Two reasons, both read from the same capability document 2-20/1C-1 already
// gate on — never re-derived, never a raw 403/402 leaked to the user:
//  - 'permission': the principal is authenticated but doesn't hold the
//    route's required permission. Honest copy + a link back to a page they
//    CAN see (the dashboard) — no upsell, this isn't a plan problem.
//  - 'locked': the route's feature isn't live for this org (plan-gated or
//    org-disabled). Routes into the existing 2-1 discovery teaser
//    (`openTeaser`) instead of reinventing the upsell affordance — the same
//    modal a locked nav row opens (components/layout/Sidebar.tsx).
export interface RouteDeniedProps {
  reason: 'permission' | 'locked';
  /** Feature key to open the 2-1 teaser for. Required when reason is 'locked'. */
  featureKey?: string | undefined;
  /** Display name passed through to the teaser (defaults to the feature key). */
  name?: string | undefined;
  backTo?: string | undefined;
  backLabel?: string | undefined;
}

export function RouteDenied({
  reason,
  featureKey,
  name,
  backTo = '/app',
  backLabel = 'Back to dashboard',
}: RouteDeniedProps): JSX.Element {
  const navigate = useNavigate();
  const { openTeaser } = useDiscovery();

  const locked = reason === 'locked';
  const Icon = locked ? LockClosedIcon : ShieldExclamationIcon;
  const heading = locked ? "This feature isn't on your plan" : "You don't have access to this page";
  const body = locked
    ? "This page is part of a plan your organization doesn't have. See what it unlocks, or ask an admin to upgrade."
    : "You don't have permission to view this page. Contact your organization admin if you think this is a mistake.";

  return (
    <div className="flex min-h-[50vh] items-center justify-center px-4 py-12">
      <div
        className="glass-card flex max-w-md flex-col items-center gap-4 p-8 text-center"
        role="region"
        aria-label={heading}
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent/20"
          aria-hidden
        >
          <Icon className="h-7 w-7 text-muted-foreground" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-foreground">{heading}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        </div>
        {locked && featureKey ? (
          <Button
            variant="secondary"
            onClick={() => openTeaser(featureKey, { name: name ?? humanizeFeatureKey(featureKey) })}
          >
            <LockClosedIcon className="h-4 w-4" aria-hidden />
            See upgrade options
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => navigate(backTo)}>
            {backLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
